import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FilterOutlined, ReloadOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { Input, InputNumber, Select, Button, Space, Row, Col } from 'antd';
import PersianDatePicker from '../PersianDatePicker';
import { numberFormatter, numberParser } from '../../utils/numberUtils';

const { Search } = Input;

/**
 * 
 * @param {object} filters - آبجکت فیلترها
 * @param {function} onFilterChange - تابع تغییر فیلتر
 * @param {function} onApply - (deprecated) تابع اعمال فیلترها - فیلتر خودکار توسط والد انجام می‌شود
 * @param {function} onReset - تابع ریست فیلترها
 * @param {object} filterOptions - گزینه‌های فیلترها
 * @param {boolean} defaultExpanded - حالت پیش‌فرض (باز/بسته)
 * @param {string} className - کلاس‌های اضافی
 */
const FilterPanel = ({
  filters = {},
  onFilterChange,
  onApply,
  onReset,
  filterOptions = {},
  defaultExpanded = false,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleFilterChange = (key, value) => {
    if (onFilterChange) {
      onFilterChange(key, value);
    }
  };

  const handleApply = () => {
    if (onApply) {
      onApply();
    }
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  const {
    statuses = [],
    priorities = [],
    actionTypes = [],
    raciRoles = [],
    perPageOptions = [10, 15, 20, 30, 50],
    sortOptions = [
      { value: 'due_at', label: 'موعد' },
      { value: 'priority', label: 'اولویت' },
      { value: 'updated_at', label: 'آخرین به‌روزرسانی' },
      { value: 'created_at', label: 'تاریخ ایجاد' },
      { value: 'status', label: 'وضعیت' },
      { value: 'title', label: 'عنوان' },
    ],
    dirOptions = [
      { value: 'asc', label: 'صعودی' },
      { value: 'desc', label: 'نزولی' },
    ],
    bookmarkedOptions = [
      { value: 1, label: 'فقط نشان‌گذاری شده' },
      { value: 0, label: 'بدون نشان' },
    ],
  } = filterOptions;

  return (
    <div
      className={`
        bg-white dark:bg-gray-800
        border border-gray-200 dark:border-gray-700
        rounded-xl overflow-hidden
        transition-all duration-300
        ${className}
      `}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FilterOutlined className="text-gray-600 dark:text-gray-400" />
          <span
            className="text-sm font-semibold text-gray-900 dark:text-gray-100"
            style={{ fontFamily: 'CustomFont' }}
          >
            فیلترهای پیشرفته
          </span>
        </div>
        {expanded ? (
          <UpOutlined className="text-gray-400 dark:text-gray-500" />
        ) : (
          <DownOutlined className="text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {expanded && (
        <div className="p-4 pt-0 border-t border-gray-200 dark:border-gray-700">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Row gutter={[12, 12]}>
              {statuses.length > 0 && (
                <Col xs={24} sm={12} md={8} lg={6}>
                  <label
                    className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    وضعیت
                  </label>
                  <Select
                    mode="multiple"
                    placeholder="همه وضعیت‌ها"
                    value={Array.isArray(filters.status) ? filters.status : filters.status ? [filters.status] : []}
                    onChange={(val) => handleFilterChange('status', val && val.length > 0 ? val : '')}
                    allowClear
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={statuses.filter(s => s.value).map((s) => ({
                      value: s.value,
                      label: s.label,
                    }))}
                  />
                </Col>
              )}

              {priorities.length > 0 && (
                <Col xs={24} sm={12} md={8} lg={6}>
                  <label
                    className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    اولویت
                  </label>
                  <Select
                    mode="multiple"
                    placeholder="همه اولویت‌ها"
                    value={Array.isArray(filters.priority) ? filters.priority : filters.priority ? [filters.priority] : []}
                    onChange={(val) => handleFilterChange('priority', val && val.length > 0 ? val : '')}
                    allowClear
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={priorities.filter(p => p.value).map((p) => ({
                      value: p.value,
                      label: p.label,
                    }))}
                  />
                </Col>
              )}

              {actionTypes.length > 0 && (
                <Col xs={24} sm={12} md={8} lg={6}>
                  <label
                    className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    نوع اقدام
                  </label>
                  <Select
                    mode="multiple"
                    placeholder="همه نوع‌ها"
                    value={Array.isArray(filters.action_type) ? filters.action_type : filters.action_type ? [filters.action_type] : []}
                    onChange={(val) => handleFilterChange('action_type', val && val.length > 0 ? val : '')}
                    allowClear
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={actionTypes.filter(t => t.value).map((t) => ({
                      value: t.value,
                      label: t.label,
                    }))}
                  />
                </Col>
              )}

              {filters.source_module !== undefined && (
                <Col xs={24} sm={12} md={8} lg={6}>
                  <label
                    className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    ماژول منبع
                  </label>
                  <Input
                    placeholder="ماژول منبع..."
                    value={filters.source_module || ''}
                    onChange={(e) => handleFilterChange('source_module', e.target.value)}
                    allowClear
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Col>
              )}

              <Col xs={24} sm={12} md={12} lg={12}>
                <label
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  بازه موعد
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <PersianDatePicker
                      value={filters.due_from}
                      onChange={(val) => handleFilterChange('due_from', val)}
                      placeholder="موعد از"
                      style={{ width: '100%', fontFamily: 'CustomFont' }}
                    />
                  </div>
                  <div className="flex-1">
                    <PersianDatePicker
                      value={filters.due_to}
                      onChange={(val) => handleFilterChange('due_to', val)}
                      placeholder="موعد تا"
                      style={{ width: '100%', fontFamily: 'CustomFont' }}
                    />
                  </div>
                </div>
              </Col>

              <Col xs={24} sm={12} md={6} lg={6}>
                <label
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  مرتب‌سازی
                </label>
                <Select
                  placeholder="مرتب‌سازی"
                  value={filters.sort || 'due_at'}
                  onChange={(val) => handleFilterChange('sort', val)}
                  style={{ width: '100%', fontFamily: 'CustomFont' }}
                  options={sortOptions.map(s => ({ value: s.value, label: s.label }))}
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={6}>
                <label
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  جهت
                </label>
                <Select
                  placeholder="جهت"
                  value={filters.dir || 'asc'}
                  onChange={(val) => handleFilterChange('dir', val)}
                  style={{ width: '100%', fontFamily: 'CustomFont' }}
                  options={dirOptions.map(d => ({ value: d.value, label: d.label }))}
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={6}>
                <label
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  نشان‌گذاری شده
                </label>
                <Select
                  placeholder="نشان‌گذاری شده"
                  value={filters.bookmarked}
                  onChange={(val) => handleFilterChange('bookmarked', val)}
                  allowClear
                  style={{ width: '100%', fontFamily: 'CustomFont' }}
                  options={bookmarkedOptions.map(b => ({ value: b.value, label: b.label }))}
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={6}>
                <label
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  فیلتر ذخیره شده
                </label>
                <InputNumber
                  placeholder="شناسه فیلتر ذخیره شده"
                  value={filters.saved_filter_id || undefined}
                  onChange={(val) => {
                    handleFilterChange('saved_filter_id', val ? Number(val) : undefined);
                  }}
                  min={1}
                  precision={0}
                  style={{ width: '100%', fontFamily: 'CustomFont' }}
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={6}>
                <label
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  تعداد در صفحه
                </label>
                <Select
                  value={filters.per_page || 15}
                  onChange={(val) => handleFilterChange('per_page', val)}
                  style={{ width: '100%', fontFamily: 'CustomFont' }}
                  options={perPageOptions.map((n) => ({
                    value: n,
                    label: `${n} مورد`,
                  }))}
                />
              </Col>
            </Row>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                style={{ fontFamily: 'CustomFont' }}
              >
                ریست فیلترها
              </Button>
            </div>
          </Space>
        </div>
      )}
    </div>
  );
};

FilterPanel.propTypes = {
  filters: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  filterOptions: PropTypes.shape({
    statuses: PropTypes.array,
    priorities: PropTypes.array,
    actionTypes: PropTypes.array,
    raciRoles: PropTypes.array,
    perPageOptions: PropTypes.array,
    sortOptions: PropTypes.array,
    dirOptions: PropTypes.array,
    bookmarkedOptions: PropTypes.array,
  }),
  defaultExpanded: PropTypes.bool,
  className: PropTypes.string,
};

export default FilterPanel;
